"use client";

import { type KeyboardEvent, useMemo, useState } from "react";
import { Form } from "react-aria-components";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { useRouter } from "next/navigation";

import {
  Header,
  InputComboBox,
  ListBoxItem,
  ListBoxSection,
} from "@jetblue/kit/input-combobox";
import { PairedButton } from "@jetblue/kit/paired-button";

import type { MainSearchBlockDialog } from "../../../dialogs/main/search-block";

const appendSearchQueryString = ({
  href,
  search = "",
}: {
  href: string;
  search: string;
}): string => {
  const url =
    !href.startsWith("http://") && !href.startsWith("https://")
      ? new URL(href, "https://www.jetblue.com")
      : new URL(href);

  url.searchParams.set("search", search.trim());

  return url.href;
};

export const MainSearchBlock = ({
  className,
  labels,
  primaryLink,
  quickLinks,
  theme,
}: MainSearchBlockDialog & {
  className?: string | undefined;
}) => {
  const router = useRouter();
  const [inputValue, setInputValue] = useState("");

  const twClassname = twMerge(
    "border-t border-t-medium-gray bg-off-white py-8 text-core-blue",
    clsx({ "bg-core-blue text-white": theme === "dark" }, className),
  );

  const links = useMemo(
    () =>
      quickLinks.links?.["@nodes"]?.map((node) => quickLinks.links?.[node]) ??
      [],
    [quickLinks.links],
  );

  const quickLinkNodes = useMemo(
    () =>
      links.map((link) => (
        <ListBoxItem
          className="flex min-h-8 items-center px-6 text-royal-blue hover:bg-royal-blue hover:text-white data-[focused]:bg-royal-blue data-[focused]:text-white"
          href={link?.href}
          id={link?.href}
          key={link?.href}
          textValue={link?.label}
        >
          {link?.label}
        </ListBoxItem>
      )),
    [links],
  );

  const searchHref = appendSearchQueryString({
    href: primaryLink.href,
    search: inputValue,
  });

  return (
    <section className={twClassname}>
      <div className="container mx-auto flex max-w-md flex-col items-center justify-center px-4">
        <h3 className="text-m-heading">{labels.heading}</h3>

        <Form
          className="mt-4 flex w-full justify-center"
          onSubmit={(event) => {
            event.preventDefault();
            router.push(searchHref);
          }}
        >
          <InputComboBox
            allowsCustomValue
            inputProperties={
              theme === "dark"
                ? {
                    className: "focus-within:border-orange",
                    inputClassName: "focus:border-orange",
                  }
                : undefined
            }
            inputValue={inputValue}
            label={labels.placeholder}
            onInputChange={setInputValue}
            onKeyUp={(event: KeyboardEvent) => {
              const ariaActiveDescendant =
                event.target instanceof Element &&
                event.target.getAttribute("aria-activedescendant");

              if (
                !ariaActiveDescendant &&
                event.key === "Enter" &&
                inputValue
              ) {
                router.push(searchHref);
              }
            }}
          >
            {quickLinkNodes.length > 0 && (
              <ListBoxSection>
                <Header>{quickLinks.label}</Header>

                {quickLinkNodes}
              </ListBoxSection>
            )}
          </InputComboBox>

          <PairedButton
            aria-label={primaryLink.label}
            type="submit"
            variant={theme === "light" ? "primary" : "secondary"}
          />
        </Form>
      </div>
    </section>
  );
};
